import { useEffect, useRef, useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, User, Menu, LogOut, Package, Store, Truck, Shield, MapPin, X, ChefHat, ListChecks } from 'lucide-react';
import NotificationBell from '@/components/NotificationBell';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Toaster } from '@/components/ui/sonner';
import CookieBanner from '@/components/CookieBanner';
import PostalCodeGate from '@/components/PostalCodeGate';
import DeliveryRhythmBanner from '@/components/DeliveryRhythmBanner';
import ClosureNotice from '@/components/ClosureNotice';
import PageNotes from '@/components/PageNotes';
import Footer from '@/components/Footer';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { useProvince } from '@/context/ProvinceContext';
import { useSiteSettings } from '@/context/SiteSettingsContext';
import { usePostalCode } from '@/hooks/usePostalCode';
import { openPostalCodeGate } from '@/lib/postalCode';
import API from '@/lib/api';

function Header() {
  const { user, logout } = useAuth();
  const { cartCount, fetchCart } = useCart();
  const { selectedProvince, provinceName, clearProvince } = useProvince();
  const { brand } = useSiteSettings();
  const postalCode = usePostalCode();
  const navigate = useNavigate();
  const location = useLocation();
  const hasFetched = useRef(false);
  const [navPages, setNavPages] = useState([]);

  useEffect(() => {
    if (user && !hasFetched.current) {
      hasFetched.current = true;
      fetchCart();
    }
    if (!user) hasFetched.current = false;
  }, [user, fetchCart]);

  useEffect(() => {
    // Custom CMS pages that opted-in to the main nav
    API.get('/nav-pages').then(r => setNavPages(r.data || [])).catch(() => setNavPages([]));
  }, []);

  const navLink = (path, label) => (
    <Link
      to={path}
      className={`font-sans text-sm tracking-wide transition-colors ${
        location.pathname === path ? 'text-brick font-medium' : 'text-stone-text/60 hover:text-stone-text'
      }`}
    >
      {label}
    </Link>
  );

  return (
    <header className="sticky top-0 bg-paper/80 backdrop-blur-md border-b border-clay/50 z-50 h-20" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-full flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/" className="font-serif text-2xl md:text-3xl font-bold text-stone-text tracking-tight flex items-center gap-2" data-testid="logo">
            {brand?.logo_url ? (
              <img
                src={brand.logo_url}
                alt={brand.site_name || 'Ernest & Marguerite'}
                className="h-9 md:h-10 w-auto object-contain"
              />
            ) : (
              <span>{brand?.site_name || 'Ernest & Marguerite'}</span>
            )}
          </Link>
          {postalCode && (!user || user.role === 'client') && (
            <button
              onClick={openPostalCodeGate}
              className="hidden md:flex items-center gap-1 bg-forest/10 text-forest hover:bg-forest/20 px-3 py-1 rounded-full text-xs font-sans font-medium transition-colors"
              data-testid="header-postal-code"
              title="Changer mon code postal de livraison">
              <MapPin className="h-3 w-3" /> Livraison · {postalCode}
            </button>
          )}
          {selectedProvince && (
            <div className="hidden md:flex items-center gap-1 bg-brick/10 text-brick px-3 py-1 rounded-full text-xs font-sans font-medium" data-testid="province-badge">
              <MapPin className="h-3 w-3" />
              {provinceName}
              <button onClick={clearProvince} className="ml-1 hover:bg-brick/20 rounded-full p-0.5" data-testid="clear-province"><X className="h-3 w-3" /></button>
            </div>
          )}
        </div>

        <nav className="hidden md:flex items-center gap-8">
          {(!user || user.role === 'client') && (
            <>
              {navLink('/produits', 'Le marché')}
              {navLink('/producteurs', 'Les producteurs')}
              {navLink('/plats-tout-prets', 'Les plats tout prêts')}
              {navLink('/journal', 'Le journal')}
              {navLink('/inspirations', 'Les listes de courses')}
              {navPages.map(p => (
                <span key={p.id}>{navLink(`/p/${p.slug}`, p.nav_label || p.title)}</span>
              ))}
            </>
          )}
          {user?.role === 'seller' && (
            <>
              {navLink('/vendeur', 'Mon espace')}
              {navLink('/producteur/' + user.id, 'Ma fiche publique')}
            </>
          )}
          {user?.role === 'traiteur' && (
            <>
              {navLink('/traiteur', 'Mon espace')}
              {navLink('/traiteur-public/' + user.id, 'Ma fiche publique')}
            </>
          )}
          {user?.role === 'livreur' && navLink('/livreur', 'Mes tournées')}
          {user?.role === 'admin' && navLink('/admin', 'Administration')}
        </nav>

        <div className="flex items-center gap-3">
          {user && user.role === 'client' && (
            <>
              <NotificationBell />
              <Link to="/panier" className="relative p-2 hover:bg-clay/50 rounded-full transition-colors" data-testid="cart-icon" aria-label="Panier">
                <ShoppingCart className="h-5 w-5 text-stone-text" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-brick text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center" data-testid="cart-count">
                    {cartCount}
                  </span>
                )}
              </Link>
            </>
          )}
          {user && user.role !== 'client' && <NotificationBell />}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="rounded-full h-10 w-10 p-0" data-testid="user-menu-trigger" aria-label="Menu utilisateur">
                  <User className="h-5 w-5 text-stone-text" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-3 py-2">
                  <p className="font-sans text-sm font-medium">{user.name}</p>
                  <p className="font-sans text-xs text-muted-foreground">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profil')} data-testid="profile-link">
                  <Package className="mr-2 h-4 w-4" /> Mon profil
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/mes-listes')} data-testid="shopping-lists-link">
                  <ListChecks className="mr-2 h-4 w-4" /> Mes listes
                </DropdownMenuItem>
                {user.role === 'seller' && (
                  <DropdownMenuItem onClick={() => navigate('/vendeur')} data-testid="seller-dashboard-link">
                    <Store className="mr-2 h-4 w-4" /> Espace vendeur
                  </DropdownMenuItem>
                )}
                {user.role === 'admin' && (
                  <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-dashboard-link">
                    <Shield className="mr-2 h-4 w-4" /> Administration
                  </DropdownMenuItem>
                )}
                {user.role === 'livreur' && (
                  <DropdownMenuItem onClick={() => navigate('/livreur')} data-testid="driver-dashboard-link">
                    <Truck className="mr-2 h-4 w-4" /> Mes tournées
                  </DropdownMenuItem>
                )}
                {user.role === 'traiteur' && (
                  <DropdownMenuItem onClick={() => navigate('/traiteur')} data-testid="traiteur-dashboard-link">
                    <ChefHat className="mr-2 h-4 w-4" /> Espace traiteur
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate('/'); }} data-testid="logout-button">
                  <LogOut className="mr-2 h-4 w-4" /> Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/connexion">
              <Button className="bg-brick text-white rounded-full px-6 font-sans text-sm hover:bg-brick/90 transition-all" data-testid="login-button">
                Connexion
              </Button>
            </Link>
          )}

          <Sheet>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" aria-label="Menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent>
              <nav className="flex flex-col gap-6 mt-8">
                {(!user || user.role === 'client') && (
                  <>
                    <Link to="/" className="font-sans text-lg">Accueil</Link>
                    <Link to="/produits" className="font-sans text-lg">Le marché</Link>
                    <Link to="/producteurs" className="font-sans text-lg">Les producteurs</Link>
                    <Link to="/plats-tout-prets" className="font-sans text-lg">Les plats tout prêts</Link>
                    <Link to="/journal" className="font-sans text-lg">Le journal</Link>
                    <Link to="/inspirations" className="font-sans text-lg">Les listes de courses</Link>
                    {navPages.map(p => (
                      <Link key={p.id} to={`/p/${p.slug}`} className="font-sans text-lg">{p.nav_label || p.title}</Link>
                    ))}
                  </>
                )}
                {user?.role === 'client' && <Link to="/panier" className="font-sans text-lg">Panier</Link>}
                {user && <Link to="/profil" className="font-sans text-lg">Mon profil</Link>}
                {user?.role === 'client' && <Link to="/mes-listes" className="font-sans text-lg">Mes listes</Link>}
                {user?.role === 'seller' && <Link to="/vendeur" className="font-sans text-lg">Mon espace vendeur</Link>}
                {user?.role === 'admin' && <Link to="/admin" className="font-sans text-lg">Administration</Link>}
                {user?.role === 'livreur' && <Link to="/livreur" className="font-sans text-lg">Mes tournées</Link>}
                {user?.role === 'traiteur' && <Link to="/traiteur" className="font-sans text-lg">Mon espace traiteur</Link>}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

export default function Layout() {
  return (
    <div className="min-h-screen bg-paper relative">
      <div
        className="fixed inset-0 pointer-events-none z-[9999] opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`
        }}
      />
      <Header />
      <DeliveryRhythmBanner />
      <ClosureNotice />
      <RoutePageNotes zone="corners" />
      <RoutePageNotes zone="banner-top" />
      <main className="min-h-[calc(100vh-5rem)]">
        <Outlet />
      </main>
      <RoutePageNotes zone="footer-banner" />
      <Footer />
      <CookieBanner />
      <PostalCodeGate />
      <Toaster position="bottom-right" />
    </div>
  );
}

/**
 * Reads the current pathname and maps it to a "page" string + optional
 * product_id so that PageNotes can fetch the right notes automatically.
 */
function RoutePageNotes({ zone }) {
  const location = useLocation();
  const { pathname } = location;
  let page = null;
  let productId = null;
  if (pathname === '/') page = 'home';
  else if (pathname === '/produits') page = 'products';
  else if (pathname.startsWith('/produit/')) {
    page = 'product_detail';
    productId = pathname.split('/produit/')[1] || null;
  } else if (pathname === '/producteurs') page = 'producers';
  else if (pathname.startsWith('/producteur/')) page = 'producer_detail';
  else if (pathname === '/panier') page = 'cart';
  else if (pathname === '/checkout') page = 'checkout';
  else if (pathname === '/plats-tout-prets') page = 'traiteurs';
  if (!page) return null;
  return <PageNotes page={page} productId={productId} zone={zone} />;
}
